import { PayloadAction } from '@reduxjs/toolkit';
import { IRecipe } from 'app/containers/Recipe/types';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { taskSaga } from './saga';
import {
  ICreateTaskPayload,
  IEditTaskPayload,
  IGetFieldAllTaskListPayload,
  IGetTaskDetailsPayload,
  IGetTaskListPayload,
  IGetTaskPlannedResourcesPayload,
  IGetTaskProgressPayload,
  IGetTaskUsedResourcesPayload,
  IRoutesWithoutTaskFilters,
  ISetFieldAllTaskListPayload,
  ISetFieldTaskListPayload,
  ISetTaskListPayload,
  ITask,
  ITaskAvailableResourceOption,
  ITaskCategory,
  ITaskFilters,
  ITaskPlannedResource,
  ITaskUsedResource,
  IUpdateTaskProgressPayload,
  TaskListSort,
  TaskState,
} from './types';

export const initialState: TaskState = {
  taskList: {
    loading: false,
    tasksArr: [],
    filters: {},
    currentPage: 1,
    total: 0,
    sort: null,
  },
  singleFieldTaskList: {
    fieldId: null,
    allFieldTasksArr: [],
    sort: null,

    loading: false,
    tasksArr: [],
    filters: {},
    currentPage: 1,
    total: 0,
  },
  categories: {
    loading: false,
    categoriesArr: [],
  },
  createTask: {
    loading: false,
  },
  taskDetails: {
    loading: false,
    task: null,
    notes: '',

    recipe: null,
  },
  taskResources: {
    loading: false,

    plannedResources: {
      resourcesArr: [],
      updatedResourcesArr: [],
    },

    thisStageUsedResourcesArr: [],

    usedResources: {
      resourcesArr: [],
      updatedResourcesArr: [],
    },

    availableResourcesArr: [],
  },

  taskProgress: {
    loading: false,
    task: null,
    notes: '',
  },
  taskProgressUpdateLoading: false,

  routesWithoutTask: {
    fields: [],
    routes: [],
    transport: [],
  },
  routesWithoutTaskFilters: {},

  checkedRouteIds: [],
};

const slice = createSlice({
  name: 'task',
  initialState,
  reducers: {
    setCheckedRouteIds(state, action: PayloadAction<number[]>) {
      state.checkedRouteIds = action.payload;
    },

    setDebouncedRoutesWithoutTaskFilters(
      state,
      action: PayloadAction<IRoutesWithoutTaskFilters>,
    ) {},

    setRoutesWithoutTaskFilters(
      state,
      action: PayloadAction<IRoutesWithoutTaskFilters>,
    ) {
      state.routesWithoutTaskFilters = action.payload;
    },

    setTaskRecipe(state, action: PayloadAction<IRecipe | null>) {
      state.taskDetails.recipe = action.payload;
    },

    getTaskPlannedResources(
      state,
      action: PayloadAction<IGetTaskPlannedResourcesPayload>,
    ) {
      state.taskResources.plannedResources.resourcesArr = [];
      state.taskResources.plannedResources.updatedResourcesArr = [];
    },
    setTaskPlannedResources(
      state,
      action: PayloadAction<ITaskPlannedResource[]>,
    ) {
      state.taskResources.plannedResources.resourcesArr = action.payload;
      state.taskResources.plannedResources.updatedResourcesArr = action.payload;
    },

    addTaskPlannedResource(state, action: PayloadAction<ITaskPlannedResource>) {
      const res = action.payload;
      const isInArr = state.taskResources.plannedResources.updatedResourcesArr.find(
        resource => resource.resourceId === res.resourceId,
      );
      if (!isInArr) {
        state.taskResources.plannedResources.updatedResourcesArr.push(res);
      }
    },

    removeTaskPlannedResource(state, action: PayloadAction<number>) {
      const resourceId = action.payload;
      const isInArr = state.taskResources.plannedResources.updatedResourcesArr.find(
        resource => resource.resourceId === resourceId,
      );
      if (isInArr) {
        state.taskResources.plannedResources.updatedResourcesArr = state.taskResources.plannedResources.updatedResourcesArr.filter(
          resource => resource.resourceId !== resourceId,
        );
      }
    },

    updateTaskPlannedResourceNorm(
      state,
      action: PayloadAction<{ resourceId: number; norm: number }>,
    ) {
      const { resourceId, norm } = action.payload;

      const resourceIdx = state.taskResources.plannedResources.updatedResourcesArr.findIndex(
        res => res.resourceId === resourceId,
      );
      if (resourceIdx !== -1) {
        state.taskResources.plannedResources.updatedResourcesArr[
          resourceIdx
        ].norm = norm;
      }
    },

    updateTaskPlannedResourcePrice(
      state,
      action: PayloadAction<{ resourceId: number; price?: number }>,
    ) {
      const { resourceId, price } = action.payload;

      const resourceIdx = state.taskResources.plannedResources.updatedResourcesArr.findIndex(
        res => res.resourceId === resourceId,
      );
      if (resourceIdx !== -1) {
        state.taskResources.plannedResources.updatedResourcesArr[
          resourceIdx
        ].price = price;
      }
    },

    setTaskThisStageUsedResources(
      state,
      action: PayloadAction<ITaskUsedResource[]>,
    ) {
      state.taskResources.thisStageUsedResourcesArr = action.payload;
    },

    toggleTaskThisStageUsedResource(
      state,
      action: PayloadAction<ITaskUsedResource>,
    ) {
      const res = action.payload;
      const isInArr = state.taskResources.thisStageUsedResourcesArr.find(
        resource => resource.resourceId === res.resourceId,
      );
      if (isInArr) {
        state.taskResources.thisStageUsedResourcesArr = state.taskResources.thisStageUsedResourcesArr.filter(
          resource => resource.resourceId !== res.resourceId,
        );
      } else {
        state.taskResources.thisStageUsedResourcesArr.push(res);
      }
    },
    updateTaskThisStageUsedResourceNorm(
      state,
      action: PayloadAction<{ resourceId: number; norm: number }>,
    ) {
      const { resourceId, norm } = action.payload;

      const resourceIdx = state.taskResources.thisStageUsedResourcesArr.findIndex(
        res => res.resourceId === resourceId,
      );
      if (resourceIdx !== -1) {
        state.taskResources.thisStageUsedResourcesArr[resourceIdx].norm = norm;
      }
    },

    getTaskUsedResources(
      state,
      action: PayloadAction<IGetTaskUsedResourcesPayload>,
    ) {
      state.taskResources.usedResources.resourcesArr = [];
      state.taskResources.usedResources.updatedResourcesArr = [];
    },
    setTaskUsedResources(state, action: PayloadAction<ITaskUsedResource[]>) {
      state.taskResources.usedResources.resourcesArr = action.payload;
      state.taskResources.usedResources.updatedResourcesArr = action.payload;
    },

    toggleTaskUsedResource(state, action: PayloadAction<ITaskUsedResource>) {
      const res = action.payload;
      const isInArr = state.taskResources.usedResources.updatedResourcesArr.find(
        resource => resource.resourceId === res.resourceId,
      );
      if (isInArr) {
        state.taskResources.usedResources.updatedResourcesArr = state.taskResources.usedResources.updatedResourcesArr.filter(
          resource => resource.resourceId !== res.resourceId,
        );
      } else {
        state.taskResources.usedResources.updatedResourcesArr.push(res);
      }
    },

    updateTaskUsedResourceNorm(
      state,
      action: PayloadAction<{ resourceId: number; norm: number }>,
    ) {
      const { resourceId, norm } = action.payload;

      const resourceIdx = state.taskResources.usedResources.updatedResourcesArr.findIndex(
        res => res.resourceId === resourceId,
      );
      if (resourceIdx !== -1) {
        state.taskResources.usedResources.updatedResourcesArr[
          resourceIdx
        ].norm = norm;
      }
    },

    getTaskCategories(state, action: PayloadAction<number>) {
      state.categories.loading = true;
    },
    setTaskCategories(state, action: PayloadAction<ITaskCategory[]>) {
      state.categories.categoriesArr = action.payload;
    },
    setTaskCategoriesLoading(state, action: PayloadAction<boolean>) {
      state.categories.loading = action.payload;
    },

    getTaskList(state, action: PayloadAction<IGetTaskListPayload>) {},
    setTaskList(state, action: PayloadAction<ISetTaskListPayload>) {
      state.taskList = { ...state.taskList, ...action.payload };
    },
    setTaskListLoading(state, action: PayloadAction<boolean>) {
      state.taskList.loading = action.payload;
    },
    setTaskListFilters(state, action: PayloadAction<ITaskFilters>) {
      state.taskList.filters = {
        ...state.taskList.filters,
        ...action.payload,
      };
    },
    setTaskListSort(state, action: PayloadAction<TaskListSort>) {
      state.taskList.sort = action.payload;
    },
    setTaskListCurrentPage(state, action: PayloadAction<number>) {
      state.taskList.currentPage = action.payload;
    },

    setFieldTaskList(state, action: PayloadAction<ISetFieldTaskListPayload>) {
      state.singleFieldTaskList = {
        ...state.singleFieldTaskList,
        ...action.payload,
      };
    },
    setFieldTaskListFilters(state, action: PayloadAction<ITaskFilters>) {
      state.singleFieldTaskList.filters = {
        ...state.singleFieldTaskList.filters,
        ...action.payload,
      };
    },
    setFieldTaskListSort(state, action: PayloadAction<TaskListSort>) {
      state.singleFieldTaskList.sort = action.payload;
    },
    setFieldTaskListCurrentPage(state, action: PayloadAction<number>) {
      state.singleFieldTaskList.currentPage = action.payload;
    },

    getFieldAllTaskList(
      state,
      action: PayloadAction<IGetFieldAllTaskListPayload>,
    ) {},
    setFieldAllTaskList(
      state,
      action: PayloadAction<ISetFieldAllTaskListPayload>,
    ) {
      state.singleFieldTaskList.allFieldTasksArr =
        action.payload.allFieldTasksArr;
      state.singleFieldTaskList.fieldId = action.payload.fieldId;
    },

    createTask(state, action: PayloadAction<ICreateTaskPayload>) {
      state.createTask.loading = true;
    },

    editTask(state, action: PayloadAction<IEditTaskPayload>) {
      state.createTask.loading = true;
    },

    setTaskCreateLoading(state, action: PayloadAction<boolean>) {
      state.createTask.loading = action.payload;
    },

    getTaskDetails(state, action: PayloadAction<IGetTaskDetailsPayload>) {},
    setTaskDetails(
      state,
      action: PayloadAction<{ task: ITask | null; notes: string }>,
    ) {
      state.taskDetails.task = action.payload.task;
      state.taskDetails.notes = action.payload.notes;
    },
    setTaskDetailsLoading(state, action: PayloadAction<boolean>) {
      state.taskDetails.loading = action.payload;
    },

    getTaskProgress(state, action: PayloadAction<IGetTaskProgressPayload>) {},

    setTaskProgress(
      state,
      action: PayloadAction<{ task: ITask; notes: string }>,
    ) {
      state.taskProgress.task = action.payload.task;
      state.taskProgress.notes = action.payload.notes;
    },
    setTaskProgressLoading(state, action: PayloadAction<boolean>) {
      state.taskProgress.loading = action.payload;
    },

    updateTaskProgress(
      state,
      action: PayloadAction<IUpdateTaskProgressPayload>,
    ) {
      state.taskProgressUpdateLoading = true;
    },

    setTaskProgressUpdateLoading(state, action: PayloadAction<boolean>) {
      state.taskProgressUpdateLoading = action.payload;
    },

    getAllAvailableTaskResources(state, action: PayloadAction<number>) {},
    setAvailableTaskResources(
      state,
      action: PayloadAction<ITaskAvailableResourceOption[]>,
    ) {
      state.taskResources.availableResourcesArr = action.payload;
    },
  },
});

export const { actions: taskActions } = slice;

export const useTaskSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectSaga({ key: slice.name, saga: taskSaga });
  return { actions: slice.actions };
};

/**
 * Example Usage:
 *
 * export function MyComponentNeedingThisSlice() {
 *  const { actions } = useTaskSlice();
 *
 *  const onButtonClick = (evt) => {
 *    dispatch(actions.someAction());
 *   };
 * }
 */
